<!--
  The page for viewing and editing APIs
-->

<template>
  <el-form
    ref="form"
    v-loading="state.loading"
    :model="viewObj"
    label-width="150px"
    :hide-required-asterisk="true">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item>
        <router-link :to="{ name: 'home', query: {view: view, compare: compare} }">
          Home
        </router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item v-if="skill==='global'">
        <router-link :to="{ name: 'global', query: {view: view, compare: compare} }">
          Global items
        </router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item v-else>
        <router-link :to="{ name: 'skill', params: {skill: skill}, query: {view: view, compare: compare} }">
          Skill summary
        </router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item>{{apiId==='new'?'Create API':'API details'}}</el-breadcrumb-item>
    </el-breadcrumb>
    <main-title
      :skill="skill"
      v-model="viewObj"
      id-field="api"
      :object-id="apiId"
      :existing="skillObj.data.apis">
      Api: {{viewObj.name}}
    </main-title>
    <view-selector
      :view="view"
      :compare="compare"
      @update="changeView"
      :object="viewObj"
      :publishable="skill === 'global'"
      :discrete="skill !== 'global' && skillObj.data.discrete_publish"
      :new-item="apiId==='new' || special && !timestamp"
      name="api"
      @save="save"
      @delete="remove"
      @test="test"
      @untest="untest"
      @publish="publish"
      @unpublish="unpublish"
      @unprod="unprod"
      @reject="reject"
      @approve="approve"
      @disapprove="disapprove"
      @restore="restore"
      ></view-selector>
    <p>
      <!-- name input -->
      <comparator :view="viewObj" :compare="compareObj"
        prop="name"
        :required="true"
        :rules="AcceptableValidator">
        <template v-slot="{ value, compare, prop, attrs, status }">
          <el-form-item :class="'comparable-' + status"
            prop="name"
            v-bind="attrs"
            label="Name">
            <el-input v-model="value.name"
              :disabled="compare"></el-input>
          </el-form-item>
        </template>
      </comparator>
    </p>
    <p>
      <el-form-item>
        <template slot="label">
          URL prefix whitelist
          <tooltip-icon placement="top">
            The lists of <em>URL prefix</em> allowed to <em>API calls</em>. Must be <em>HTTPS</em>.<br/>
              Example: <code>https://my-api.com/v2/</code>
          </tooltip-icon>
        </template>
        <el-form-sub
          prop="whitelist"
          :model="{whitelist:viewWhitelist}">
          <comparator :view="viewWhitelist" :compare="compareWhitelist">
            <template v-slot="{ value, index, compare }">
              <el-form-errors>
                <el-button-group style="width: 100%">
                  <comparable default=""
                    :ignore="value.temporary"
                    :rules="[URLValidator, HTTPSValidator, list2editor.duplicateValueRule(viewWhitelist, value.key, 'Duplicate URL')]"
                    :prop="'whitelist.' + index + '.value'"
                    :required="!value.temporary || index === 0">
                    <template v-slot="{ view, compare, attrs, prop, status }">
                      <el-form-small-item :class="'comparable-' + status"
                        :prop="prop"
                        v-bind="attrs"
                        field="url">
                        <el-input
                          v-model="value.value"
                          placeholder="whitelist URL"
                          :disabled="compare"></el-input>
                      </el-form-small-item>
                    </template>
                  </comparable>
                  <el-button
                    :icon="value.temporary ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
                    :disabled="compare || value.temporary"
                    @click="list2editor.deleteKey(viewWhitelist, value.key)"
                    :tabindex="-1"></el-button>
                </el-button-group>
              </el-form-errors>
            </template>
          </comparator>
        </el-form-sub>
      </el-form-item>
    </p>
    <p>
      <el-form-item>
        <template slot="label">
          URL prefix blacklist
          <tooltip-icon placement="top">
            The lists of <em>URL prefix</em> forbidden to <em>API calls</em>.<br/>
            Example: <code>https://my-api.com/v2/restricted</code>
          </tooltip-icon>
        </template>
        <el-form-sub
          prop="blacklist"
          :model="{blacklist:viewBlacklist}">
          <comparator :view="viewBlacklist" :compare="compareBlacklist">
            <template v-slot="{ value, index, compare }">
              <el-form-errors>
                <el-button-group style="width: 100%">
                  <comparable default=""
                    :ignore="value.temporary"
                    :rules="[URLValidator, HTTPSValidator, list2editor.duplicateValueRule(viewBlacklist, value.key, 'Duplicate URL')]"
                    :prop="'blacklist.' + index + '.value'"
                    :required="!value.temporary">
                    <template v-slot="{ view, compare, attrs, prop, status }">
                      <el-form-small-item :class="'comparable-' + status"
                        :prop="prop"
                        v-bind="attrs"
                        field="url">
                        <el-input
                          v-model="value.value"
                          placeholder="blacklist URL"
                          :disabled="compare"></el-input>
                      </el-form-small-item>
                    </template>
                  </comparable>
                  <el-button
                    :icon="value.temporary ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
                    :disabled="compare || value.temporary"
                    @click="list2editor.deleteKey(viewBlacklist, value.key)"
                    :tabindex="-1"></el-button>
                </el-button-group>
              </el-form-errors>
            </template>
          </comparator>
        </el-form-sub>
      </el-form-item>
    </p>
    <p>
      <comparator :view="viewObj" :compare="compareObj"
        prop="name"
        :required="true">
        <template v-slot="{ value, compare, prop, attrs, status }">
          <el-form-item :class="'comparable-' + status"
            prop="level"
            v-bind="attrs"
            label="Level">
            <el-select :value="value.level" @input="setLevel"
              :disabled="compare">
              <el-option value="server">
                Server
                <tooltip-icon placement="right">
                  Authenticated on the <em>server</em> and shared by all the <em>users</em>.
                </tooltip-icon>
              </el-option>
              <el-option value="user">
                User
                <tooltip-icon placement="right">
                  Authenticated on the <em>user's device</em> and personal.
                </tooltip-icon>
              </el-option>
            </el-select>
          </el-form-item>
        </template>
      </comparator>
    </p>
    <comparator :view="viewObj" :compare="compareObj"
      prop="level">
      <template v-slot="{ value, other, view }">
        <div>
          <template v-if="value.level === 'server'">
            <fieldset>
              <legend>
                <comparator :view="viewObj" :compare="compareObj"
                  field="headers-checked" :prop="value => !!value.headers" diff>
                  <template v-slot="{ value, status, compare }">
                    <el-checkbox v-if="value.level === 'server'"
                      style="margin-left: "
                      :disabled="compare"
                      :class="'comparable-' + status"
                      :value="!!value.headers"
                      @input="v => setServerAuthorization('headers', defaultHeaders, v)">
                      <tooltip-icon placement="right">
                        Custom <em>headers</em> will be passed with any <em>request</em>,<br/> as a mean of authentication
                      </tooltip-icon>
                      Authenticate client with custom headers
                    </el-checkbox>
                  </template>
                </comparator>
              </legend>
              <el-form-item v-if="viewHeaders && viewHeaders.length || compareHeaders && compareHeaders.length"
                class="fieldset-container"
                label="Headers"
                label-width="80px">
                <el-form-sub
                  prop="headers"
                  :model="{headers:viewHeaders}">
                  <comparator :view="viewHeaders" :compare="compareHeaders">
                    <template v-slot="{ value, index, compare }">
                      <el-form-errors>
                        <el-button-group style="width: 100%">
                          <comparable prop="name" default=""
                            :ignore="value.temporary"
                            :required="!value.temporary || index === 0"
                            :rules="[HeaderNameValidator, HeaderAllowedValidator, dict2editor.duplicateNameRule(viewHeaders, value.key, 'Duplicate header')]">
                            <template v-slot="{ view, compare, attrs, status }">
                              <el-form-small-item :class="'comparable-' + status"
                                style="max-width: 200px"
                                :prop="view && 'headers.'+index+'.name'"
                                v-bind="attrs"
                                field="name">
                                <el-input
                                  v-model="value.name"
                                  placeholder="header name"
                                  :disabled="compare"></el-input>
                              </el-form-small-item>
                            </template>
                          </comparable>
                          <comparable
                            prop="value" default=""
                            :ignore="value.temporary"
                            :required="!value.temporary || index === 0">
                            <template v-slot="{ view, compare, attrs }">
                              <el-form-small-item :class="'comparable-' + status"
                                :prop="view && 'headers.'+index+'.value'"
                                v-bind="attrs"
                                field="value">
                                <el-input
                                  v-model="value.value"
                                  :disabled="compare"
                                  placeholder="header value"></el-input>
                              </el-form-small-item>
                            </template>
                          </comparable>
                          <el-button
                            :icon="value.temporary ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
                            :disabled="compare || value.temporary"
                            @click="dict2editor.deleteKey(viewHeaders, value.key)"
                            :tabindex="-1"></el-button>
                        </el-button-group>
                      </el-form-errors>
                    </template>
                  </comparator>
                </el-form-sub>
              </el-form-item>
            </fieldset>
            <fieldset>
              <legend>
                <comparator :view="viewObj" :compare="compareObj"
                  field="tls-checked" :prop="value => !!value.tls" diff>
                  <template v-slot="{ value, status, compare }">
                    <el-checkbox v-if="value.level === 'server'"
                      :disabled="compare"
                      :class="'comparable-' + status"
                      :value="!!value.tls"
                      @input="v => setServerAuthorization('tls', defaultTls, v)">
                      <tooltip-icon placement="right">
                        Custom <em>client TLS certificates</em> will be used for any <em>request</em>,<br/> as a mean of authentication
                      </tooltip-icon>
                      Authenticate client with custom TLS cerrtificates
                    </el-checkbox>
                  </template>
                </comparator>
              </legend>
              <comparator :view="viewObj" :compare="compareObj"
                field="tls-exists" :prop="value => !!value.tls">
                <template v-slot="{ value }">
                  <div v-if="value.level === 'server' && value.tls"
                    class="fieldset-container">
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="tls.public" :rules="[]" :required="true">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'server' && value.tls"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="Public TLS key"
                          v-bind="attrs">
                          <el-input v-model="value.tls.public"
                            type="textarea"
                            :disabled="compare"></el-input>
                        </el-form-item>
                      </template>
                    </comparator>
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="tls.private" :rules="[]" :required="true">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'server' && value.tls"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="Private TLS key"
                          v-bind="attrs">
                          <el-input v-model="value.tls.private"
                            type="textarea"
                            :disabled="compare"></el-input>
                        </el-form-item>
                      </template>
                    </comparator>
                  </div>
                </template>
              </comparator>
            </fieldset>
            <fieldset>
              <legend>
                <comparator :view="viewObj" :compare="compareObj"
                  field="oauth2_client-checked" :prop="value => !!value.oauth2_client" diff>
                  <template v-slot="{ value, status, compare }">
                    <el-checkbox v-if="value.level === 'server'"
                      :disabled="compare"
                      :class="'comparable-' + status"
                      :value="!!value.oauth2_client"
                      @input="v => setServerAuthorization('oauth2_client', defaultOauth2Client, v)">
                      <tooltip-icon placement="right">
                        An <em>OAuth2 client token</em> will be requested, and used with any <em>request</em>,<br/> as a mean of authentication
                      </tooltip-icon>
                      Authenticate client with OAuth2 credential grant
                    </el-checkbox>
                  </template>
                </comparator>
              </legend>
              <comparator :view="viewObj" :compare="compareObj"
                field="oauth2_client-exists" :prop="value => !!value.oauth2_client">
                <template v-slot="{ value }">
                  <div v-if="value.level === 'server' && value.oauth2_client"
                    class="fieldset-container">
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="oauth2_client.url" :rules="[URLValidator, HTTPSValidator]" :required="true">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'server' && value.oauth2_client"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="URL"
                          v-bind="attrs">
                          <el-input v-model="value.oauth2_client.url"
                            :disabled="compare"></el-input>
                        </el-form-item>
                      </template>
                    </comparator>
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="oauth2_client.client_id" :rules="[]" :required="true">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'server' && value.oauth2_client"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="Client ID"
                          v-bind="attrs">
                          <el-input v-model="value.oauth2_client.client_id"
                            :disabled="compare"></el-input>
                        </el-form-item>
                      </template>
                    </comparator>
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="oauth2_client.client_secret" :rules="[]" :required="true">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'server' && value.oauth2_client"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="Client secret"
                          v-bind="attrs">
                          <el-input v-model="value.oauth2_client.client_secret"
                            :disabled="compare"></el-input>
                        </el-form-item>
                      </template>
                    </comparator>
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="oauth2_client.scope" :rules="[]">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'server' && value.oauth2_client"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="Scope"
                          v-bind="attrs">
                          <el-tag-input
                            :value="value.oauth2_client.scope ? value.oauth2_client.scope.split(' ') : []"
                            @input="v => { value.oauth2_client.scope = v.join(' ') }"
                            :rules="ScopeValidator"
                            placeholder="">
                          </el-tag-input>
                        </el-form-item>
                      </template>
                    </comparator>
                  </div>
                </template>
              </comparator>
            </fieldset>
          </template>
          <template v-else-if="value.level === 'user'">
            <fieldset>
              <legend>
                <comparator :view="viewObj" :compare="compareObj"
                  field="basic_auth-checked" :prop="value => !!value.basic_auth" diff>
                  <template v-slot="{ value, status, compare }">
                    <el-radio v-if="value.level === 'user'"
                      :disabled="compare"
                      :class="'comparable-' + status"
                      label="basic_auth"
                      :value="getUserAuthorization(value)"
                      @input="v => v && setUserAuthorization('basic_auth')">
                      <tooltip-icon placement="right">
                        The <em>user</em> wil be asked for <em>username-password</em>,<br/> and a <em>Basic Auth Authorization header</em> will be passed to any <em>request</em>,<br/> as a mean of authentication.
                      </tooltip-icon>
                      Authenticate user with basic auth
                    </el-radio>
                  </template>
                </comparator>
              </legend>
              <comparator :view="viewObj" :compare="compareObj"
                field="basic_auth-exists" :prop="value => !!value.basic_auth">
                <template v-slot="{ value }">
                  <div v-if="value.level === 'user' && value.basic_auth">
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="basic_auth.server_api">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item
                          :prop="prop"
                          v-bind="attrs">
                          <template slot="label">
                            Server API
                            <tooltip-icon placement="top">
                              The <em>OAuth2 request</em> will receive authorization headers defined in the <em>server-level API</em>
                            </tooltip-icon>
                          </template>
                          <el-button-group style="display: inline-flex !important;">
                            <el-filterable-select
                              v-model="value.basic_auth.server_api"
                              :options="serverApiOptions"
                              value-field="api"
                              label-field="name"
                              :disabled="compare"
                              clearable>
                            </el-filterable-select>
                            <router-link v-if="value.basic_auth.server_api"
                              style="padding: 12px"
                              :to="{
                                name: 'api',
                                params: {skill: $route.params.skill, api: value.basic_auth.server_api},
                                query: $route.query,
                              }"
                              target="_blank"
                              class="el-button"
                              :tabindex="-1">
                              <i class="el-icon-link"></i>
                            </router-link>
                            <el-button v-else
                              style="padding: 12px"
                              icon="el-icon-link"
                              disabled
                              :tabindex="-1">
                            </el-button>
                          </el-button-group>
                        </el-form-item>
                      </template>
                    </comparator>
                  </div>
                </template>
              </comparator>
            </fieldset>
            <fieldset>
              <legend>
                <comparator :view="viewObj" :compare="compareObj"
                  field="oauth2_password-checked" :prop="value => !!value.oauth2_password" diff>
                  <template v-slot="{ value, status, compare }">
                    <el-radio v-if="value.level === 'user'"
                      :disabled="compare"
                      :class="'comparable-' + status"
                      label="oauth2_password"
                      :value="getUserAuthorization(value)"
                      @input="v => v && setUserAuthorization('oauth2_password', defaultOauth2Password)">
                      <tooltip-icon placement="right">
                        The <em>user</em> wil be asked for <em>username-password</em>,<br/> which will be used to request an <em>Oauth2 token</em> and used with any <em>request</em>,<br/> as a mean of authentication.
                      </tooltip-icon>
                      Authenticate user with OAuth2 password grant
                    </el-radio>
                  </template>
                </comparator>
              </legend>
              <comparator :view="viewObj" :compare="compareObj"
                field="oauth2_password-exists" :prop="value => !!value.oauth2_password">
                <template v-slot="{ value }">
                  <div v-if="value.level === 'user' && value.oauth2_password"
                    class="fieldset-container">
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="oauth2_password.url" :rules="[URLValidator, HTTPSValidator, whitelistedValidator]" :required="true">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'user' && value.oauth2_password"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="URL"
                          v-bind="attrs">
                          <el-input v-model="value.oauth2_password.url"
                            :disabled="compare"></el-input>
                        </el-form-item>
                      </template>
                    </comparator>
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="oauth2_password.scope" :rules="[]">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'user' && value.oauth2_password"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="Scope"
                          v-bind="attrs">
                          <el-tag-input
                            :value="value.oauth2_password.scope ? value.oauth2_password.scope.split(' ') : []"
                            @input="v => { value.oauth2_password.scope = v.join(' ') }"
                            :rules="ScopeValidator"
                            placeholder="">
                          </el-tag-input>
                        </el-form-item>
                      </template>
                    </comparator>
                    <el-form-item label="Client authentication">
                      <comparator :view="viewObj" :compare="compareObj"
                        field="basic_auth-client_auth-type" :prop="(v) => 'server_api' in v.oauth2_password" diff>
                        <template v-slot="{ value, status, compare }">
                          <div style="margin-left: 20px;">
                            <el-radio v-if="value.level === 'user'"
                              :disabled="compare"
                              :class="'comparable-' + status"
                              :label="true"
                              :value="'client_secret' in value.oauth2_password"
                              @input="v => v && setClientAuthorization('oauth2_password', 'client_secret')">
                                <tooltip-icon placement="top">
                                  The <em>OAuth2 request</em> will receive the <em>client credentials</em>
                                </tooltip-icon>
                                client credentials
                            </el-radio>
                            <el-radio v-if="value.level === 'user'"
                              :disabled="compare"
                              :class="'comparable-' + status"
                              :label="true"
                              :value="'server_api' in value.oauth2_password"
                              @input="v => v && setClientAuthorization('oauth2_password', 'server_api')">
                                <tooltip-icon placement="top">
                                  The <em>OAuth2 request</em> will receive authorization headers defined in the <em>server-level API</em>
                                </tooltip-icon>
                                server API
                            </el-radio>
                          </div>
                        </template>
                      </comparator>
                    </el-form-item>
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="oauth2_password.server_api">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'user' && value.oauth2_password && 'server_api' in value.oauth2_password"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="Server API"
                          v-bind="attrs">
                          <el-button-group style="display: inline-flex !important;">
                            <el-filterable-select
                              v-model="value.oauth2_password.server_api"
                              :options="serverApiOptions"
                              value-field="api"
                              label-field="name"
                              :disabled="compare"
                              clearable>
                            </el-filterable-select>
                            <router-link v-if="value.oauth2_password.server_api"
                              style="padding: 12px"
                              :to="{
                                name: 'api',
                                params: {skill: $route.params.skill, api: value.oauth2_password.server_api},
                                query: $route.query,
                              }"
                              target="_blank"
                              class="el-button"
                              :tabindex="-1">
                              <i class="el-icon-link"></i>
                            </router-link>
                            <el-button v-else
                              style="padding: 12px"
                              icon="el-icon-link"
                              disabled
                              :tabindex="-1">
                            </el-button>
                          </el-button-group>
                        </el-form-item>
                      </template>
                    </comparator>
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="oauth2_password.client_id" :rules="[]" :required="true">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'user' && value.oauth2_password && 'client_id' in value.oauth2_password"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="Client ID"
                          v-bind="attrs">
                          <el-input v-model="value.oauth2_password.client_id"
                            :disabled="compare"></el-input>
                        </el-form-item>
                      </template>
                    </comparator>
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="oauth2_password.client_secret" :rules="[]" :required="true">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'user' && value.oauth2_password && 'client_secret' in value.oauth2_password"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="Client secret"
                          v-bind="attrs">
                          <el-input v-model="value.oauth2_password.client_secret"
                            :disabled="compare"></el-input>
                        </el-form-item>
                      </template>
                    </comparator>
                  </div>
                </template>
              </comparator>
            </fieldset>
            <fieldset>
              <legend>
                <comparator :view="viewObj" :compare="compareObj"
                  field="oauth2_code-checked" :prop="value => !!value.oauth2_code" diff>
                  <template v-slot="{ value, status, compare }">
                    <el-radio v-if="value.level === 'user'"
                      :disabled="compare"
                      :class="'comparable-' + status"
                      label="oauth2_code"
                      :value="getUserAuthorization(value)"
                      @input="v => v && setUserAuthorization('oauth2_code', defaultOauth2Code)">
                      <tooltip-icon placement="right">
                        The <em>user</em> wil be redirected to an OAuth2 authentication page,<br/> whose returned code will be used to request an <em>Oauth2 token</em> and used with any <em>request</em>,<br/> as a mean of authentication.
                      </tooltip-icon>
                      Authenticate user with OAuth2 implicit grant
                    </el-radio>
                  </template>
                </comparator>
              </legend>
              <comparator :view="viewObj" :compare="compareObj"
                field="oauth2_code-exists" :prop="value => !!value.oauth2_code">
                <template v-slot="{ value, view }">
                  <div v-if="value.level === 'user' && value.oauth2_code"
                    class="fieldset-container">
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="oauth2_code.authorization_url" :rules="[URLValidator, HTTPSValidator]" :required="true">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'user' && value.oauth2_code"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="Auhorization URL"
                          v-bind="attrs">
                          <el-input v-model="value.oauth2_code.authorization_url"
                            :disabled="compare"></el-input>
                          <el-alert
                            type="warning"
                            show-icon
                            :closable="false">
                            <template slot="title">
                              The callback URL must be authorized: <code>{{callbackUrl}}</code>
                            </template>
                          </el-alert>
                        </el-form-item>
                      </template>
                    </comparator>
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="oauth2_code.url" :rules="[URLValidator, HTTPSValidator, whitelistedValidator]" :required="true">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'user' && value.oauth2_code"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="Code URL"
                          v-bind="attrs">
                          <el-input v-model="value.oauth2_code.url"
                            :disabled="compare"></el-input>
                        </el-form-item>
                      </template>
                    </comparator>
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="oauth2_code.scope" :rules="[]">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'user' && value.oauth2_code"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="Scope"
                          v-bind="attrs">
                          <el-tag-input
                            :value="value.oauth2_code.scope ? value.oauth2_code.scope.split(' ') : []"
                            @input="v => { value.oauth2_code.scope = v.join(' ') }"
                            :rules="ScopeValidator"
                            placeholder="">
                          </el-tag-input>
                        </el-form-item>
                      </template>
                    </comparator>
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="oauth2_code.client_id" :rules="[]" :required="true">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'user' && value.oauth2_code"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="Client ID"
                          v-bind="attrs">
                          <el-input v-model="value.oauth2_code.client_id"
                            :disabled="compare"></el-input>
                        </el-form-item>
                      </template>
                    </comparator>
                    <el-form-item label="Client authentication">
                      <comparator :view="viewObj" :compare="compareObj"
                        field="basic_auth-client_auth-type" :prop="(v) => 'server_api' in v.oauth2_code" diff>
                        <template v-slot="{ value, status, compare }">
                          <div style="margin-left: 20px;">
                            <el-radio v-if="value.level === 'user'"
                              :disabled="compare"
                              :class="'comparable-' + status"
                              :label="true"
                              :value="'client_secret' in value.oauth2_code"
                              @input="v => v && setClientAuthorization('oauth2_code', 'client_secret')">
                              <tooltip-icon placement="top">
                                The <em>OAuth2 request</em> will receive the <em>client credentials</em>
                              </tooltip-icon>
                              client credentials
                            </el-radio>
                            <el-radio v-if="value.level === 'user'"
                              :disabled="compare"
                              :class="'comparable-' + status"
                              :label="true"
                              :value="'server_api' in value.oauth2_code"
                              @input="v => v && setClientAuthorization('oauth2_code', 'server_api')">
                              <tooltip-icon placement="top">
                                The <em>OAuth2 request</em> will receive authorization headers defined in the <em>server-level API</em>
                              </tooltip-icon>
                              server API
                            </el-radio>
                          </div>
                        </template>
                      </comparator>
                    </el-form-item>
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="oauth2_code.server_api">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'user' && value.oauth2_code && 'server_api' in value.oauth2_code"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="Server API"
                          v-bind="attrs">
                          <el-button-group style="display: inline-flex !important;">
                            <el-filterable-select
                              v-model="value.oauth2_code.server_api"
                              :options="serverApiOptions"
                              value-field="api"
                              label-field="name"
                              :disabled="compare"
                              clearable>
                            </el-filterable-select>
                            <router-link v-if="value.oauth2_code.server_api"
                              style="padding: 12px"
                              :to="{
                                name: 'api',
                                params: {skill: $route.params.skill, api: value.oauth2_code.server_api},
                                query: $route.query,
                              }"
                              target="_blank"
                              class="el-button"
                              :tabindex="-1">
                              <i class="el-icon-link"></i>
                            </router-link>
                            <el-button v-else
                              style="padding: 12px"
                              icon="el-icon-link"
                              disabled
                              :tabindex="-1">
                            </el-button>
                          </el-button-group>
                        </el-form-item>
                      </template>
                    </comparator>
                    <comparator :view="viewObj" :compare="compareObj"
                      prop="oauth2_code.client_secret" :rules="[]" :required="true">
                      <template v-slot="{ value, compare, prop, attrs }">
                        <el-form-item v-if="value.level === 'user' && value.oauth2_code && 'client_secret' in value.oauth2_code"
                          :class="'comparable-' + status"
                          :prop="prop"
                          label="Client secret"
                          v-bind="attrs">
                          <el-input v-model="value.oauth2_code.client_secret"
                            :disabled="compare"></el-input>
                        </el-form-item>
                      </template>
                    </comparator>
                  </div>
                </template>
              </comparator>
            </fieldset>
            <el-form-small-item v-if="view" prop="level"
              :rules="userAuthenticationValidator"></el-form-small-item>
          </template>
        </div>
      </template>
    </comparator>
  </el-form>
</template>

<script>
import * as dict2editor from '../../utils/dict2editor.js'
import * as list2editor from '../../utils/list2editor.js'
import State from '../../stores/state.js'
import ViewSelector from '../ViewSelector.vue'
import ImportExport from '../ImportExport.vue'
import {
  AcceptableValidator, HeaderNameValidator, HeaderAllowedValidator, URLValidator, HTTPSValidator, ScopeValidator, urlWhitelistSummary, urlMatchWhitelist,
} from '../../utils/validators'
import ElTagInput from '../element/ElTagInput.vue'
import ElFilterableSelect from '../element/ElFilterableSelect.vue'

const FIELDS = {
  server: ['headers', 'tls', 'oauth2_client'],
  user: ['basic_auth', 'oauth2_password', 'oauth2_code'],
}
const CLIENT_SECRET_FIELDS = {
  'client_id': true,
  'client_secret': true,
  'server_api': false,
}
const SERVER_API_FIELDS = {
  'client_id': false,
  'client_secret': false,
  'server_api': true,
}
const SERVER_API_WITH_CLIENT_FIELDS = {
  'client_id': true,
  'client_secret': false,
  'server_api': true,
}
const CLIENT_AUTHORIZATION_FIELDS = {
  'basic_auth': {
    'server_api': SERVER_API_FIELDS,
  },
  'oauth2_password': {
    'client_secret': CLIENT_SECRET_FIELDS,
    'server_api': SERVER_API_FIELDS,
  },
  'oauth2_code': {
    'client_secret': CLIENT_SECRET_FIELDS,
    'server_api': SERVER_API_WITH_CLIENT_FIELDS,
  },
}
const CLIENT_AUTHORIZATION_DEFAULTS = {
  'client_id': '',
  'client_secret': '',
  'server_api': null,
}

export default {
  name: 'ApiPage',
  components: {
    ViewSelector,
    ImportExport,
    ElTagInput,
    ElFilterableSelect,
  },

  props: {
    skill: String,
    api: String, // 'new' to create a new api
    view: String,
    compare: String,
  },
  data () {
    return {
      state: State.state,
      apiId: null, // also updated once the api is created
      skillObj: State.empty,
      viewObj: {
        name: '',
        level: 'server',
      },
      compareObj: undefined,
      AcceptableValidator,
      HeaderNameValidator,
      HeaderAllowedValidator,
      URLValidator,
      HTTPSValidator,
      whitelistedValidator: {
        trigger: 'blur',
        validator: (rule, value, callback) => {
          if (!value) return callback()
          this.serverApiPromise.then(({ data, whitelist }) => {
            if (!data || this.clientAutorizationComputed.type !== 'server_api') return callback()
            return urlMatchWhitelist(value, whitelist, data.whitelist && data.whitelist.length, callback)
          })
        },
      },
      userAuthenticationValidator: {
        trigger: 'submit',
        validator: (rule, value, callback) => {
          if (this.viewObj.level !== 'user') return callback()
          for (let field of FIELDS.user) {
            if (this.viewObj[field]) return callback()
          }
          return callback(new Error('a user authentication method is required'))
        },
      },
      ScopeValidator,
      dict2editor,
      list2editor,
      viewHeaders: [], // for editing headers as a list
      viewWhitelist: list2editor.emptyEditor(),
      viewBlacklist: list2editor.emptyEditor(),
      saveCopy: {},
      clientAutorizationType: 'client_secret',
      clientAutorizationValues: {
        ...CLIENT_AUTHORIZATION_DEFAULTS,
      },
      serverApiPromise: Promise.resolve({}),
    }
  },
  inject: ['onerror'],
  // load data
  created () {
    this.apiId = this.api
    Promise.all([
      this.loadSkill(),
      this.loadView(),
      this.loadCompare(),
    ]).finally(() => {
      this.$anchorsLoaded()
    })
  },
  computed: {
    // shortcuts for useful properties
    status () { return this.viewObj.status || null },
    timestamp () { return this.viewObj.timestamp || null },
    published () { return this.viewObj.published || null },
    approved () { return this.viewObj.approved || null },
    special () {
      return this.viewObj.api
        ? this.viewObj.api.length !== 32 : false
    },

    compareHeaders () {
      return this.compareObj && this.compareObj.level === 'server' && this.compareObj.headers
        ? dict2editor.updateEditor(this.compareObj.headers) : undefined
    },
    compareWhitelist () {
      return this.compareObj ? list2editor.updateEditor(this.compareObj.whitelist) : undefined
    },
    compareBlacklist () {
      return this.compareObj ? list2editor.updateEditor(this.compareObj.blacklist) : undefined
    },

    shallowCopy () {
      return { ...(this.viewObj || {}) }
    },

    callbackUrl () {
      return 'TODO'
    },

    clientAutorizationComputed () {
      if (this.viewObj.level !== 'user') return {}
      for (let [field, types] of Object.entries(CLIENT_AUTHORIZATION_FIELDS)) {
        let value = this.viewObj[field]
        if (!value || typeof value !== 'object') continue
        for (let [type, fields] of Object.entries(types)) {
          if (type in value) {
            return {
              field: field,
              type: type,
              fields: Object.fromEntries(Object.entries(fields)
                .filter(([_, keep]) => keep)
                .map(([field, _]) => [field, value[field] ||
                  CLIENT_AUTHORIZATION_DEFAULTS[field]])),
            }
          }
        }
      }
      return {}
    },

    serverApiOptions () {
      let apis = (this.skillObj.data.apis || [])
        .filter(api => api.level === 'server' && api.api !== this.api)
      apis.sort((a, b) => (a.name > b.name) - (a.name < b.name) || b.local - a.local)
      return apis
    },

    serverApiParams () {
      return [this.skill || '', this.clientAutorizationValues.server_api || '', this.view || ''].join('/')
    },
  },
  watch: {
    // Update the headers from the list
    viewHeaders: {
      handler () {
        if (this.viewObj.headers) {
          this.viewObj.headers = dict2editor.editorUpdated(this.viewObj.headers, this.viewHeaders)
        }
      },
      deep: true,
    },
    // Update the headers list from the dict
    'viewObj.headers' () {
      this.viewHeaders = this.viewObj.headers ? dict2editor.updateEditor(this.viewObj.headers, this.viewHeaders) : undefined
    },
    // Update the whitelist from the list
    viewWhitelist: {
      handler () {
        this.viewObj.whitelist = list2editor.editorUpdated(this.viewObj.whitelist, this.viewWhitelist)
      },
      deep: true,
    },
    // Update the whitelist list from the list
    'viewObj.whitelist' () {
      this.viewWhitelist = list2editor.updateEditor(this.viewObj.whitelist, this.viewWhitelist)
    },
    // Update the blacklist from the list
    viewBlacklist: {
      handler () {
        this.viewObj.blacklist = list2editor.editorUpdated(this.viewObj.blacklist, this.viewBlacklist)
      },
      deep: true,
    },
    // Update the blacklist list from the list
    'viewObj.blacklist' () {
      this.viewBlacklist = list2editor.updateEditor(this.viewObj.blacklist, this.viewBlacklist)
    },
    // user connected, reload everything
    'state.user' () {
      this.loadSkill()
      this.loadView()
      this.loadCompare()
    },
    // skill changed, reload everything
    skill () {
      this.loadSkill()
      this.loadView()
      this.loadCompare()
    },
    // api change, reload API data
    api () {
      if (this.apiId === this.api) return
      this.apiId = this.api
      this.loadView()
      this.loadCompare()
    },
    // view change, reload view data
    view () {
      this.loadSkill()
      this.loadView()
    },
    // compare change, reload compare data
    compare () { this.loadCompare() },

    shallowCopy (value) {
      for (let [n, v] of Object.entries(value)) {
        this.saveCopy[n] = v
      }
    },

    clientAutorizationComputed ({ field, type, fields }) {
      if (!type) return
      if (field.startsWith('oauth2_')) {
        this.clientAutorizationType = type
      }
      for (let [f, v] of Object.entries(fields)) {
        this.clientAutorizationValues[f] = v
      }
    },

    serverApiParams () {
      let server_api = this.clientAutorizationValues.server_api
      if (!server_api) {
        this.serverApiPromise = Promise.resolve({})
        return
      }
      const view = this.view
      this.serverApiPromise = State.getApi(this.skill, server_api, this.view)
        .then((data) => {
          let whitelist = data ? urlWhitelistSummary(data.whitelist, data.blacklist) : []
          return { data, view, whitelist }
        })
        .catch(this.onerror)
    },

    viewObj () {
      if (this.viewObj.level === 'user') {
        for (let field of FIELDS.user) {
          if (this.viewObj[field]) {
            this.restoreClientAuthorization(this.viewObj[field], field)
          }
        }
      }
    },
  },
  methods: {
    // clears the value to change the level
    setLevel (level) {
      if (this.viewObj.level !== level) {
        if (this.viewObj.level) {
          for (let field of FIELDS[this.viewObj.level]) {
            this.$delete(this.viewObj, field)
            break
          }
        }
      }
      this.viewObj.level = level
    },
    // default values when checking some fields
    defaultTls () {
      return {
        public: '',
        private: '',
      }
    },
    defaultHeaders () {
      return {
        Authorization: '',
      }
    },
    defaultOauth2Client () {
      return {
        url: '',
        client_id: '',
        client_secret: '',
        scope: '',
      }
    },
    defaultOauth2Password () {
      return {
        url: '',
        scope: '',
      }
    },
    defaultOauth2Code () {
      return {
        authorization_url: '',
        url: '',
        scope: '',
      }
    },
    // used for the server-level checkbox
    setServerAuthorization (field, deflt, checked) {
      if (checked) {
        this.$set(this.viewObj, field,
          this.viewObj[field] || this.saveCopy[field] || deflt && deflt() || {})
      } else {
        this.$delete(this.viewObj, field)
      }
    },
    // used for the user-level radio
    getUserAuthorization (value) {
      for (let field of FIELDS.user) {
        if (value[field]) return field
      }
      return null
    },
    setUserAuthorization (field, deflt) {
      for (let f of FIELDS.user) {
        if (f === field) {
          let value = this.saveCopy[f] || deflt && deflt() || {}
          this.restoreClientAuthorization(value, f)
          this.$set(this.viewObj, f, value)
        } else {
          this.$delete(this.viewObj, f)
        }
      }
    },
    // used for client-auth radio on user-level
    setClientAuthorization (field, type) {
      this.clientAutorizationType = type
      this.restoreClientAuthorization(this.viewObj[field], field)
    },
    restoreClientAuthorization (value, field) {
      const type = field.startsWith('oauth2_')
        ? this.clientAutorizationType : 'server_api'
      const fields = CLIENT_AUTHORIZATION_FIELDS[field][type]
      for (let [f, keep] of Object.entries(fields)) {
        if (keep) {
          this.$set(value, f, this.clientAutorizationValues[f])
        } else {
          this.$delete(value, f)
        }
      }
    },
    // load skill data
    loadSkill () {
      let skill = this.skill, view = this.view
      return State.getSkill(skill, view).then(data => {
        if (skill !== this.skill || view !== this.view) return
        this.skillObj = data
      }).catch(this.onerror)
    },
    // load view API data
    loadView () {
      if (this.apiId === 'new') {
        this.viewObj = {
          name: '',
          level: 'server',
        }
        return Promise.resolve()
      } else {
        let skill = this.skill, api = this.apiId, view = this.view
        return State.getApi(skill, api, view).then(data => {
          if (skill !== this.skill || api !== this.apiId ||
            view !== this.view) return
          this.viewObj = data
        }).catch(onerror)
      }
    },
    // load compare API data
    loadCompare () {
      if (this.apiId === 'new' || !this.compare) {
        this.compareObj = undefined
        return Promise.resolve()
      } else {
        let skill = this.skill, api = this.apiId, view = this.compare
        return State.getApi(skill, api, view).then(data => {
          if (skill !== this.skill || api !== this.apiId ||
            view !== this.compare) return
          this.compareObj = data
        }).catch(onerror)
      }
    },
    // delete api
    remove () {
      State.removeApi(this.skill, this.apiId).then(() => {
        this.$router.replace('/skills/' + this.skill +
          '/apis/new')
      }).catch(this.onerror)
    },
    // save api
    save () {
      this.$refs.form.validate((valid, errors) => {
        if (valid) {
          let promise
          if (this.apiId === 'new') {
            promise = State.createApi(this.skill, this.viewObj, this.viewObj.api)
              .then(data => {
                this.$message({
                  message: 'API created',
                  type: 'success',
                })
                this.apiId = data.api
                this.viewObj = data
                this.$router.replace({
                  name: 'api',
                  params: { skill: this.skill, api: this.apiId },
                })
              })
          } else {
            promise = State.saveApi(this.skill, this.apiId, this.viewObj)
              .then(data => {
                this.$message({
                  message: 'API saved',
                  type: 'success',
                })
                this.viewObj = data
              })
          }
          promise.then(() => {
            return State.getSkill(this.skill, null, true)
          }).catch(this.onerror)
        } else {
          console.error(errors)
          this.$message.error('Please correct the errors and try again')
        }
      })
    },
    // remove api from test cache
    untest () {
      State.untestApi(this.skill, this.apiId).then(() => {
        this.$message({
          message: 'Test cleared',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // test api
    test () {
      State.testApi(this.skill, this.apiId).then(() => {
        this.$message({
          message: 'API in test',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // update apiObj fields after publishing
    updateApi (data) {
      if (!data || !data.api) return
      for (let field in data) this.viewObj[field] = data[field]
    },
    // remove api from published cache
    unpublish () {
      State.unpublishApi(this.skill, this.apiId, this.published).then(data => {
        this.updateApi(data)
        this.$message({
          message: 'API unpublished',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // remove api from approved cache
    unprod () {
      State.publishApi(this.skill, this.apiId, false).then(data => {
        this.updateApi(data)
        this.$message({
          message: 'API unproded',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // publish api
    publish () {
      State.publishApi(this.skill, this.apiId, this.timestamp).then(data => {
        this.updateApi(data)
        this.$message({
          message: 'API published',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // disapprove api
    disapprove () {
      State.disapproveApi(this.skill, this.apiId, this.approved).then(data => {
        this.updateApi(data)
        this.$message({
          message: 'API disapproved',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // reject api
    reject () {
      State.rejectApi(this.skill, this.apiId, this.published).then(data => {
        this.updateApi(data)
        this.$message({
          message: 'API rejected',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // approve api
    approve () {
      State.approveApi(this.skill, this.apiId, this.published).then(data => {
        this.updateApi(data)
        this.$message({
          message: data.published ? 'API approved' : 'Deletion approved',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // restore published or approved api to dev
    restore (version) {
      let timestamp = version === 'pub' ? this.published : this.approved
      let name = version === 'pub' ? 'Published' : 'Approved'
      console.log('restore', version, timestamp, name)
      State['restore' + name + 'API'](this.skill, this.apiId, timestamp).then(() => {
        this.$message({
          message: name + ' api restored',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // change view or compare
    changeView (view, compare) {
      this.$router.push({ query: { view: view, compare: compare } })
    },
  },
}
</script>
